import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { AgencyShowcaseCard } from '@/components/agencies/AgencyShowcaseCard';

type AgencyCard = {
  id: string;
  name: string;
  logo_url: string | null;
  city: string | null;
  cover_url: string | null;
  properties_count: number;
  members_count: number;
  rating: number;
  reviews: number;
};

// Curated fallback cover images (real estate / architecture)
const FALLBACK_COVERS = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=70',
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=70',
  'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=70',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=70',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=70',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=70',
  'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=70',
  'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=70',
];

export function TitaniumAgencies() {
  const navigate = useNavigate();

  const { data: agencies } = useQuery<AgencyCard[]>({
    queryKey: ['top-agencies-cards'],
    queryFn: async () => {
      const { data: ags } = await supabase
        .from('agencies')
        .select('id, name, logo_url, city')
        .eq('is_verified', true)
        .order('created_at', { ascending: false })
        .limit(8);

      const list = ags || [];
      const enriched = await Promise.all(
        list.map(async (a, i) => {
          // Try a property image as cover
          const { data: members } = await supabase
            .from('agent_profiles')
            .select('user_id, rating, total_reviews')
            .eq('agency_id', a.id);
          const userIds = (members || []).map((m: any) => m.user_id).filter(Boolean);

          let properties_count = 0;
          if (userIds.length) {
            const { count } = await supabase
              .from('properties')
              .select('id', { count: 'exact', head: true })
              .in('user_id', userIds)
              .eq('status', 'active')
              .eq('approval_status', 'approved');
            properties_count = count || 0;
          }

          const ratings = (members || []).map((m: any) => Number(m.rating || 0)).filter((r) => r > 0);
          const reviews = (members || []).reduce((s: number, m: any) => s + (m.total_reviews || 0), 0);
          const rating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;

          return {
            id: a.id,
            name: a.name,
            logo_url: a.logo_url,
            city: a.city,
            cover_url: FALLBACK_COVERS[i % FALLBACK_COVERS.length],
            properties_count,
            members_count: userIds.length,
            rating,
            reviews,
          } as AgencyCard;
        })
      );
      return enriched;
    },
  });

  if (!agencies?.length) return null;

  return (
    <section className="bg-background">
      <div className="container py-6 md:py-7">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold tracking-tight">Top Real Estate Agencies</h2>
          </div>
          <button
            onClick={() => navigate('/agencies')}
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline shrink-0 mt-1"
          >
            View all agencies <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <AgenciesCarousel agencies={agencies} />
      </div>
    </section>
  );
}

function AgenciesCarousel({ agencies }: { agencies: AgencyCard[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  // Duplicate the list so right-to-left auto-scroll loops seamlessly
  const loop = [...agencies, ...agencies];

  // Smooth auto-scroll from right to left
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    const tick = () => {
      if (!paused && el) {
        el.scrollLeft += 0.5;
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, agencies.length]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth + 12 : 240;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div
      className="relative group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => scrollBy(-1)}
        className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background border border-border shadow-md flex items-center justify-center hover:bg-muted transition"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => scrollBy(1)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background border border-border shadow-md flex items-center justify-center hover:bg-muted transition"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto scroll-smooth no-scrollbar mx-4"
        style={{ scrollbarWidth: 'none' }}
      >
        {loop.map((a, idx) => (
          <div
            key={`${a.id}-${idx}`}
            className="shrink-0 basis-[calc((100%-2.25rem)/2)] sm:basis-[calc((100%-2.25rem)/3)] lg:basis-[calc((100%-3rem)/5)]"
          >
            <AgencyShowcaseCard
              id={a.id}
              name={a.name}
              logoUrl={a.logo_url}
              coverUrl={a.cover_url}
              city={a.city}
              propertiesCount={a.properties_count}
              membersCount={a.members_count}
              rating={a.rating}
              reviews={a.reviews}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

